import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const CHAT_DRAFT_PREFIX = "chat_draft_";
const DRAFT_SAVE_DELAY_MS = 400;
const DRAFT_MAX_LENGTH = 10000;

const isQuotaExceededError = (error) =>
  error?.name === "QuotaExceededError" || error?.code === 22 || error?.code === 1014;

const getStorage = () => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
};

const normalizeDraft = (value) => String(value ?? "").slice(0, DRAFT_MAX_LENGTH);

export const buildChatDraftKey = (chatId, userId) => {
  if (!chatId) return null;
  return `${CHAT_DRAFT_PREFIX}${userId || "anonymous"}_${chatId}`;
};

const readDraftValue = (key) => {
  if (!key) return "";
  const storage = getStorage();
  if (!storage) return "";
  try {
    return storage.getItem(key) ?? "";
  } catch {
    return "";
  }
};

const removeDraftValue = (key) => {
  if (!key) return;
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    // localStorage can fail in restrictive browsers; ignore to avoid UI crash.
  }
};

const writeDraftValue = (key, value) => {
  if (!key) return;
  const storage = getStorage();
  if (!storage) return;
  const normalizedValue = normalizeDraft(value);
  try {
    if (!normalizedValue.trim()) {
      storage.removeItem(key);
      return;
    }
    storage.setItem(key, normalizedValue);
  } catch (error) {
    if (isQuotaExceededError(error)) {
      console.warn("Chat draft save skipped: localStorage quota exceeded");
    }
  }
};

export const clearAllChatDrafts = () => {
  const storage = getStorage();
  if (!storage) return;
  try {
    const keysToRemove = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key?.startsWith(CHAT_DRAFT_PREFIX)) keysToRemove.push(key);
    }
    keysToRemove.forEach((key) => {
      try {
        storage.removeItem(key);
      } catch {
        // Ignore per-key errors to finish best-effort cleanup.
      }
    });
  } catch {
    // Ignore storage access failures.
  }
};

export const useDraftStorage = ({ chatId, userId }) => {
  const draftKey = useMemo(() => buildChatDraftKey(chatId, userId), [chatId, userId]);
  const saveTimeoutRef = useRef(null);
  const latestDraftRef = useRef("");
  const latestKeyRef = useRef(draftKey);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    const currentDraft = readDraftValue(draftKey);
    latestKeyRef.current = draftKey;
    latestDraftRef.current = currentDraft;
    setDraft(currentDraft);
  }, [draftKey]);

  const saveDraftSync = useCallback((value) => {
    const key = latestKeyRef.current;
    const normalizedValue = normalizeDraft(value);
    latestDraftRef.current = normalizedValue;
    writeDraftValue(key, normalizedValue);
  }, []);

  const clearDraft = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    latestDraftRef.current = "";
    setDraft("");
    removeDraftValue(latestKeyRef.current);
  }, []);

  const saveDraft = useCallback(
    (value) => {
      const currentKey = latestKeyRef.current;
      const normalizedValue = normalizeDraft(value);
      setDraft(normalizedValue);
      latestDraftRef.current = normalizedValue;

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      if (!normalizedValue.trim()) {
        removeDraftValue(currentKey);
        saveTimeoutRef.current = null;
        return;
      }

      saveTimeoutRef.current = setTimeout(() => {
        writeDraftValue(currentKey, normalizedValue);
        saveTimeoutRef.current = null;
      }, DRAFT_SAVE_DELAY_MS);
    },
    []
  );

  useEffect(() => {
    const handleBeforeUnload = () => {
      saveDraftSync(latestDraftRef.current);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
    };
  }, [saveDraftSync]);

  return {
    draft,
    saveDraft,
    clearDraft,
  };
};
